import { useEffect, useState } from 'react';
import { useAuth, isTTC } from '../../hooks/useAuth';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import BillingAddressEditor from '../../components/BillingAddressEditor';
import ShipToAssigner from '../../components/ShipToAssigner';

const EMPTY = { email:'', password:'', first_name:'', last_name:'', phone:'', role:'customer_user', customer_id:'', show_pricing:1 };

export default function UsersAdminPage() {
  const { user, canImpersonate, impersonate, refreshMe } = useAuth();

  const TIER = { customer_user: 1, customer_admin: 2, ttc_staff: 3, ttc_superadmin: 4 };
  const tierOf = (u) => (u?.is_programmer_admin ? 5 : (TIER[u?.role] || 0));
  const myTier = tierOf(user);

  const doImpersonate = async (u2) => {
    if (!window.confirm(`Impersonate ${u2.first_name} ${u2.last_name}? Your session will act as this user until you return. This is logged.`)) return;
    try { await impersonate(u2.id); window.location.href = '/'; }
    catch (e) { toast.error(e.response?.data?.error || 'Could not impersonate'); }
  };

  const toggleProgrammer = async (u2) => {
    const grant = !u2.is_programmer_admin;
    if (!window.confirm(`${grant ? 'Grant' : 'Revoke'} Programmer Admin for ${u2.email}?`)) return;
    try {
      await api.patch(`/auth/programmer/${u2.id}`, { grant });
      toast.success(grant ? 'Programmer Admin granted' : 'Programmer Admin revoked');
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };

  // Who can this admin archive/delete: not self; TTC can act on anyone,
  // a customer admin only on non-TTC users in their own customer.
  const canManage = (u2) => u2.id !== user.id && (
    isTTC(user) ||
    (user.role === 'customer_admin' && u2.customer_id === user.customer_id && !['ttc_superadmin','ttc_staff'].includes(u2.role))
  );

  const archiveUser = async (u2, archived) => {
    if (!window.confirm(`${archived ? 'Archive' : 'Restore'} ${u2.first_name} ${u2.last_name}?`)) return;
    try {
      await api.post(`/api/customers/users/${u2.id}/archive`, { archived });
      toast.success(archived ? 'User archived' : 'User restored');
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };

  const deleteUser = async (u2) => {
    if (!window.confirm(`Permanently delete ${u2.email}? This can't be undone. (Users with orders or address history can't be deleted — archive them instead.)`)) return;
    try {
      await api.delete(`/api/customers/users/${u2.id}`);
      toast.success('User deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Delete failed'); }
  };

  const resetPassword = async (u2) => {
    if (!window.confirm(`Reset password for ${u2.email}? A new temporary password will be generated.`)) return;
    try {
      const r = await api.post(`/api/customers/users/${u2.id}/reset-password`);
      window.prompt(`Temporary password for ${u2.email} — copy and share it securely. Have them change it after signing in:`, r.data.temporary_password);
      toast.success('Password reset');
    } catch (e) { toast.error(e.response?.data?.error || 'Reset failed'); }
  };
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [pwHint, setPwHint] = useState('');
  const [pwPolicy, setPwPolicy] = useState(null);

  // Generated to satisfy the configured policy, so a generated password is
  // never rejected by the server that just handed out the rules.
  const generatePw = () => {
    const min = pwPolicy?.min_length || 12;
    const len = Math.max(min, 14);
    const lower = 'abcdefghijkmnopqrstuvwxyz', upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const digit = '23456789', symbol = '!@#$%^&*?-_+=';
    const all = lower + upper + digit + symbol;
    const rand = (pool) => pool[window.crypto.getRandomValues(new Uint32Array(1))[0] % pool.length];
    const chars = [rand(lower), rand(upper), rand(digit), rand(symbol)];
    while (chars.length < len) chars.push(rand(all));
    for (let i = chars.length - 1; i > 0; i--) {
      const j = window.crypto.getRandomValues(new Uint32Array(1))[0] % (i + 1);
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    const pw = chars.join('');
    set('password', pw);
    setPwHint(pw);
  };

  useEffect(() => {
    api.get('/auth/password-policy').then(r => setPwPolicy(r.data)).catch(() => {});
  }, []);

  const load = async () => {
    if (isTTC(user)) {
      const [u, c] = await Promise.all([
        api.get('/api/customers').then(r => r.data.customers),
        api.get('/api/customers').then(r => r.data.customers),
      ]);
      // load all users across all customers
      const allUsers = [];
      for (const c of u) {
        const r = await api.get(`/api/customers/${c.id}/users`);
        allUsers.push(...r.data.users.map(x => ({...x, customer_name: c.name})));
      }
      setUsers(allUsers);
      setCustomers(u);
    } else {
      const [u, cust] = await Promise.all([
        api.get(`/api/customers/${user.customer_id}/users`).then(r => r.data.users),
        api.get(`/api/customers/${user.customer_id}`).then(r => [r.data.customer]),
      ]);
      setUsers(u);
      setCustomers(cust);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({...f,[k]:v}));

  const save = async () => {
    setSaving(true);
    try {
      if (form.id) {
        await api.patch(`/api/customers/users/${form.id}`, form);
        toast.success('User updated');
      } else {
        const payload = { ...form, customer_id: form.customer_id || user.customer_id };
        await api.post('/auth/users', payload);
        toast.success('User created');
      }
      setModal(false);
      setForm(EMPTY);
      load();
    } catch(e) {
      toast.error(e.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const ttcRoles = ['ttc_superadmin','ttc_staff','customer_admin','customer_user'];
  const customerRoles = ['customer_admin','customer_user'];
  const availRoles = isTTC(user) ? ttcRoles : customerRoles;

  return (
    <>
      <div className="topbar">
        <h2>Users</h2>
        <button className="btn btn-gold btn-sm" onClick={() => { setForm(EMPTY); setModal(true); }}>
          <Plus size={15} /> New User
        </button>
      </div>
      <div className="page">
        <div className="card" style={{padding:0}}>
          {loading ? <div className="loading">Loading…</div> : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    {isTTC(user) && <th>Customer</th>}
                    <th>Status</th>
                    <th>Pricing</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u2 => (
                    <tr key={u2.id}>
                      <td>{u2.first_name} {u2.last_name}</td>
                      <td className="text-sm">{u2.email}</td>
                      <td className="text-sm">{u2.role?.replace(/_/g,' ')}{u2.is_programmer_admin && <span className="badge badge-approved" style={{marginLeft:6}}>Programmer</span>}</td>
                      {isTTC(user) && <td className="text-sm">{u2.customer_name || '—'}</td>}
                      <td><span className={`badge badge-${u2.active?'active':'inactive'}`}>{u2.active?'Active':'Inactive'}</span></td>
                      <td>{u2.show_pricing ? '✓' : '—'}</td>
                      <td>
                        <button className="btn btn-ghost btn-sm" onClick={() => { setForm({...u2, password:''}); setModal(true); }}>Edit</button>
                        {canImpersonate && u2.id !== user.id && u2.active && tierOf(u2) < myTier && (
                          <button className="btn btn-ghost btn-sm" onClick={() => doImpersonate(u2)}>Impersonate</button>
                        )}
                        {user?.is_programmer_admin && ['ttc_superadmin','ttc_staff'].includes(u2.role) && (
                          <button className="btn btn-ghost btn-sm" onClick={() => toggleProgrammer(u2)}>
                            {u2.is_programmer_admin ? 'Revoke Prog.' : 'Make Prog.'}
                          </button>
                        )}
                        {canManage(u2) && (u2.active
                          ? <button className="btn btn-ghost btn-sm" onClick={() => archiveUser(u2, true)}>Archive</button>
                          : <button className="btn btn-ghost btn-sm" onClick={() => archiveUser(u2, false)}>Restore</button>)}
                        {canManage(u2) && (
                          <button className="btn btn-ghost btn-sm" onClick={() => resetPassword(u2)}>Reset PW</button>
                        )}
                        {canManage(u2) && (
                          <button className="btn btn-ghost btn-sm" style={{ color: 'var(--brand-red)' }} onClick={() => deleteUser(u2)}>Delete</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {modal && (
          <div className="modal-overlay" onClick={() => setModal(false)}>
            <div className="modal" onClick={e=>e.stopPropagation()}>
              <div className="modal-header">
                <h3>{form.id ? 'Edit User' : 'New User'}</h3>
                <button className="btn btn-ghost btn-sm" onClick={() => setModal(false)}>✕</button>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">First Name</label>
                  <input className="form-input" value={form.first_name} onChange={e=>set('first_name',e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Last Name</label>
                  <input className="form-input" value={form.last_name} onChange={e=>set('last_name',e.target.value)} /></div>
              </div>
              <div className="form-group"><label className="form-label">Phone</label>
                <input className="form-input" value={form.phone || ''} onChange={e=>set('phone',e.target.value)} placeholder="(555) 555-1234" /></div>
              <div className="form-group"><label className="form-label">Email<span className="required">*</span></label>
                <input className="form-input" type="email" value={form.email} onChange={e=>set('email',e.target.value)} disabled={!!form.id} /></div>
              {!form.id && (
                <div className="form-group"><label className="form-label">Password</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input className="form-input" type="text" value={form.password}
                      onChange={e => { set('password', e.target.value); setPwHint(''); }}
                      placeholder="Type a password or generate one" />
                    <button type="button" className="btn btn-secondary btn-sm" onClick={generatePw}>Generate</button>
                  </div>
                  {pwPolicy?.description && <p className="form-hint">{pwPolicy.description}</p>}
                  {pwHint && <p className="form-hint">Generated — copy and share it securely: <strong>{pwHint}</strong></p>}
                </div>
              )}
              <div className="form-row">
                <div className="form-group"><label className="form-label">Role</label>
                  <select className="form-select" value={form.role} onChange={e=>set('role',e.target.value)}>
                    {availRoles.map(r=><option key={r} value={r}>{r.replace(/_/g,' ')}</option>)}
                  </select>
                </div>
                <div className="form-group"><label className="form-label">Show Pricing</label>
                  <select className="form-select" value={form.show_pricing} onChange={e=>set('show_pricing',parseInt(e.target.value))}>
                    <option value={1}>Yes</option>
                    <option value={0}>No</option>
                  </select>
                </div>
              </div>
              {isTTC(user) && !form.id && (
                <div className="form-group"><label className="form-label">Customer</label>
                  <select className="form-select" value={form.customer_id} onChange={e=>set('customer_id',e.target.value)}>
                    <option value="">— TTC Internal —</option>
                    {customers.filter(c=>c.active).map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              {form.id && (
                <div className="form-group"><label className="form-label">Status</label>
                  <select className="form-select" value={form.active} onChange={e=>set('active',parseInt(e.target.value))}>
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              )}
              {/* Ship-to assignment — only for customer users; staff and customer
                  admins are never restricted, so showing it would mislead. */}
              {form.id && form.role === 'customer_user' && (parseInt(form.customer_id) || user.customer_id) && (
                <ShipToAssigner userId={form.id} />
              )}
              {form.id && (() => {
                const cid = parseInt(form.customer_id) || user.customer_id;
                const cust = customers.find(c => c.id === cid);
                const perUser = (cust?.billing_mode || (!isTTC(user) ? undefined : 'customer')) === 'user';
                return perUser ? <BillingAddressEditor customerId={cid} scope="user" refId={form.id} label="Billing address for this user" /> : null;
              })()}
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={save} disabled={saving}>Save User</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
